import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../store/reducer-main';
import { Button, Card, CardBody, CardFooter, CardHeader, Checkbox, Divider, Input } from '@nextui-org/react';
import StripeCheckOutForm, { StripeCheckOutFormRef } from '../../components/common/StripeCheckOutForm';
import { Elements } from '@stripe/react-stripe-js';
import LoadingPage from '../common/LoadingPage';
import { useNavigate } from 'react-router';
import { resetPurchaseSession } from '../../../store/purchaseSession/slice';
import { processUserMembership } from '../../../service/profile';
import { initProcessingPayment } from '../../../store/processingPayment/slice';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { StripeCustomerBillingData } from '../../../types/stripe';
import { updateStripeCustomerBillingData } from '../../../service/common/stripe';
import ProductRecapCard from '../../components/purchase/ProductRecapCard';
import PageLayout from '../../components/common/PageLayout';
import PurchaseSessionStepsHeader from '../../components/common/PurchaseSessionStepsHeader';
import { PurchaseSessionStep } from '../../../constants/purchaseSession';
import Typography from '../../components/common/Typography';
import { updateStripeCustomer } from '../../../store/profile/slice';
import IconButton from '../../components/common/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { signOut } from '../../../service/common/firebase';
import theme from '../../theme';

export default function PurchasePage() {

  const checkoutRef = React.createRef<StripeCheckOutFormRef>();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile } = useSelector((state: StoreState) => state.profile);
  const { session } = useSelector((state: StoreState) => state.purchaseSession);
  const { stripeInstance } = useSelector((state: StoreState) => state.stripe);
  
  const [name, setName] = React.useState<string>('');
  const [surname, setSurname] = React.useState<string>('');
  const [postalCode, setPostaleCode] = React.useState<string>('');
  const [isCompany, setIsCompany] = React.useState<boolean>(true);
  const [companyName, setCompanyName] = React.useState<string>('');
  const [vatNumber, setVatNumber] = React.useState<string>('');
  const [address, setAddress] = React.useState<string>('');
  const [city, setCity] = React.useState<string>('');
  const [sdi, setSdi] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [paymentMethodCompleted, setPaymentMethodCompleted] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!session) navigate('/purchase/products');
  }, [])

  const clientSecret = React.useMemo(() => {
    return session?.paymentIntent.client_secret;
  }, [session]);

  const product = React.useMemo(() => {
    return session?.product;
  }, [session]);

  const submitDisabled = React.useMemo(() => {
    if (name.length === 0) return true;
    if (surname.length === 0) return true;
    if (postalCode.length === 0) return true;
    if (companyName.length === 0) return true;
    if (vatNumber.length === 0) return true;
    if (address.length === 0) return true;
    if (city.length === 0) return true;
    if (sdi.length === 0) return true;
    if (!paymentMethodCompleted) return true;
    return false;
  }, [name, surname, postalCode, companyName, vatNumber, address, city, sdi, paymentMethodCompleted]);

  const onPaymentElementChange = (
    e: StripePaymentElementChangeEvent
  ) => {
    setPaymentMethodCompleted(e.complete);
  }

  const constructBillingDataObj = (): StripeCustomerBillingData => {
    return {
      name,
      surname,
      postalCode,
      isCompany,
      companyName,
      vatNumber,
      address,
      city,
      sdi
    }
  }

  const submit = async () => {
    if (userProfile && checkoutRef.current) {
      const { paymentElements, stripeInstance } = checkoutRef.current;
      if (stripeInstance && paymentElements && session) {
        setLoading(true);
        const stripeCustomerBillingData = constructBillingDataObj();
        dispatch(updateStripeCustomer(stripeCustomerBillingData));
        processUserMembership(userProfile.uid, stripeInstance, paymentElements, session, () => {
          dispatch(resetPurchaseSession()); // TODO: Dopo implemetazione di iscrizione con webhook, rivalutare come pulire sessione
          dispatch(initProcessingPayment({uid: userProfile.uid, subscription: session.subscription}));
        });
      }
    }
  }

  if (!session) return (
    <LoadingPage />
  )
  
  return (
    <PageLayout bg='background'>
      <PurchaseSessionStepsHeader
        selectedStep={PurchaseSessionStep.PAYMENT} />
      <div className='flex flex-col px-16 gap-8'>
        <div className='flex items-center gap-4 mt-16'>
          <IconButton
            onClick={signOut}
            icon={<ArrowBackIcon style={{color: theme.color.background}}/>} />
          <Typography variant='headline2'>Pagamento</Typography>
        </div>
        <div className='flex flex-row gap-40'>
        <div className='flex flex-col gap-4 flex-1' style={{maxWidth: '900px', minWidth: '600px'}}>
          <Input value={name} onValueChange={setName} label="Nome" />
          <Input value={surname} onValueChange={setSurname} label="Cognome" />
          <Input value={postalCode} onValueChange={setPostaleCode} label="Codice postale" />
          <Checkbox isSelected={isCompany} onValueChange={setIsCompany}>Sono un'azienda</Checkbox>
          <Input value={companyName} onValueChange={setCompanyName} label="Ragione Sociale" />
          <Input value={vatNumber} onValueChange={setVatNumber} label="Partita Iva" />
          <Input value={address} onValueChange={setAddress} label="Indirizzo" />
          <Input value={city} onValueChange={setCity} label="Città" />
          <Input value={sdi} onValueChange={setSdi} label="SDI" />
          {
            clientSecret && (
              <Elements stripe={stripeInstance} options={{clientSecret}}>
                <StripeCheckOutForm ref={checkoutRef} onPaymentElementChange={onPaymentElementChange} />
              </Elements>
            )
          }
        </div>
        <div style={{maxWidth: '900px', minWidth: '650px', marginBottom: 'auto'}}>
        {
          product && (
            <ProductRecapCard 
              disabled={submitDisabled}
              onSubmit={submit}
              product={product}
              submitTitle='ABBONATI'
              submitVariant='primary'
              loading={loading}/>
          )
        }
        </div>
        </div>
      </div>
    </PageLayout>
  )
}